import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ContributorGroupToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useContributorGroupMutations } from '~/hooks/use-contributor-groups';
import {
	contributorGroupSchema,
	EMPTY_CONTRIBUTOR_GROUP,
	tokenToForm,
	type ContributorGroupFormValues
} from '~/lib/schemas/contributor-group';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Raw token being edited, or null for a new row. */
	initial: ContributorGroupToken | null;
}

/** Add / edit a contributor group (Ομάδα Συντελεστών). */
export function ContributorGroupEditDialog({ open, onOpenChange, initial }: Props) {
	const { t } = useTranslation();
	const { create, update } = useContributorGroupMutations();
	const form = useForm<ContributorGroupFormValues>({
		resolver: zodResolver(contributorGroupSchema) as Resolver<ContributorGroupFormValues>,
		defaultValues: initial ? tokenToForm(initial) : EMPTY_CONTRIBUTOR_GROUP,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	useEffect(() => {
		if (open) reset(initial ? tokenToForm(initial) : EMPTY_CONTRIBUTOR_GROUP);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync({ form: values, original: initial });
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('contributors.groups.editTitle') : t('contributors.groups.addTitle')}</DialogTitle>
					<DialogDescription>{t('contributors.groups.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-3">
						<FormField label={t('contributors.groups.columns.name')} error={formState.errors.nm?.message}>
							<Input {...register('nm')} maxLength={100} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
						</FormField>
						<FormField label={t('contributors.groups.columns.code')} error={formState.errors.cd?.message}>
							<Input {...register('cd')} maxLength={50} aria-invalid={Boolean(formState.errors.cd)} />
						</FormField>
					</div>
					<FormField label={t('contributors.groups.columns.description')} error={formState.errors.desc?.message}>
						<Textarea {...register('desc')} rows={2} aria-invalid={Boolean(formState.errors.desc)} />
					</FormField>
					<div className="grid grid-cols-2 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="cg-iv">{t('contributors.groups.columns.visible')}</Label>
							<Switch id="cg-iv" checked={watch('iv')} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
						</div>
						<FormField label={t('contributors.groups.columns.sort')} error={formState.errors.si?.message}>
							<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
